import InsightPie from "./InsightPie";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { fmtNum } from "./formatters";

/**
 * Conversion by digital channel. Interactive: clicking a slice/legend row
 * fires `onSelectChannel`. Meta/Google totals include leads recovered
 * from Dynamics CRM when the pixel didn't fire (ad-blockers, cookies,
 * mobile Safari) — flagged with an asterisk on the channel name.
 */
export default function ChannelPieChart({
  data, selectedChannel, onSelectChannel, enrichedByChannel = {},
}) {
  const items = data || [];
  const total = items.reduce((s, d) => s + (d.converted || 0), 0);
  const totalEnriched = Object.values(enrichedByChannel).reduce((s, n) => s + (n || 0), 0);

  return (
    <InsightPie
      testId="channel-pie-chart"
      title="Conversiones por Canal Digital"
      subtitle={
        totalEnriched > 0
          ? `${total} unidades atribuidas · click en un canal para filtrar campañas y timeseries · * incluye ${fmtNum(totalEnriched)} leads recuperados vía Dynamics cuando el pixel no disparó`
          : `${total} unidades atribuidas · click en un canal para filtrar campañas y timeseries`
      }
      header={
        <ScorecardInfoTip
          label="Canal digital"
          formula="Unidades vendidas atribuidas por canal (Meta Ads, Google Ads, Directo)."
          extra={
            "Cada venta se atribuye a UN solo canal (el del primer lead que hizo match). Total = unidades digitales del período. " +
            "El asterisco (*) al lado del canal indica leads adicionales recuperados de Dynamics CRM cuando el pixel de la plataforma no disparó (ad-blockers, cookies bloqueadas, tráfico móvil). Estos leads se identifican por match de UTM y se cuentan una sola vez (dedupe por email + teléfono)."
          }
          source="sales ∩ (leads_digital + Dynamics recovery por UTM)"
        />
      }
      data={items}
      getKey={(d) => d.channel}
      getValue={(d) => d.converted || 0}
      centerValue={`${total}`}
      centerLabel="CONVERTIDOS"
      emptyMessage="Sin leads digitales en este período."
      selectedKey={selectedChannel}
      onSelect={(k) => onSelectChannel && onSelectChannel(k)}
      renderLegendRow={({ item, color, key, total: t, isHover, isSelected, isDim, onEnter, onLeave, onClick }) => {
        const pct = t > 0 ? ((item.converted / t) * 100).toFixed(1) : "0.0";
        const noConv = item.converted === 0;
        const testKey = String(key).replace(/\s+/g, '-').toLowerCase();
        const enriched = enrichedByChannel[item.channel] || 0;
        return (
          <div key={key}
            className="flex items-center justify-between py-1.5 px-2 rounded"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onClick={onClick}
            data-testid={`pie-legend-${testKey}`}
            style={{
              cursor: "pointer",
              background: isSelected ? "#fef9c3" : (isHover ? "#f8fafc" : "transparent"),
              border: isSelected ? "1px solid #fde047" : "1px solid transparent",
              opacity: (isDim ? 0.5 : 1) * (noConv ? 0.7 : 1),
              transition: "opacity 0.15s ease",
            }}
          >
            <div className="flex items-center gap-2 text-xs">
              <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: "inline-block" }} />
              <span className="font-bold text-gray-900">
                {item.channel}
                {enriched > 0 && (
                  <span
                    title={`Incluye ${fmtNum(enriched)} leads recuperados vía Dynamics (pixel no disparó)`}
                    className="ml-0.5 font-extrabold"
                    style={{ color: "#1d4ed8" }}
                    data-testid={`enriched-badge-${testKey}`}
                  >
                    *
                  </span>
                )}
              </span>
              {isSelected && (
                <span className="text-[9px] font-extrabold uppercase tracking-wider" style={{ color: "#854d0e" }}>
                  · activo
                </span>
              )}
            </div>
            <div className="text-xs font-bold tabular-nums text-right">
              {noConv ? (
                <>
                  <span className="text-gray-400">0 conv.</span>
                  <div className="text-[9px] font-semibold text-gray-500">
                    {`${fmtNum(item.leads)} leads · sin conversión aún`}
                  </div>
                </>
              ) : (
                <span className="text-gray-700">
                  {`${item.converted}`} <span className="text-gray-400">{`(${pct}%)`}</span>
                </span>
              )}
            </div>
          </div>
        );
      }}
    />
  );
}
